import { useMemo } from "react"
import groupBy from "lodash/groupBy"
import { format } from "date-fns"
import { GeneratedProductImage, Product } from "@/shared/types/product"

export interface GalleryImage {
  assetId: string
  url: string
  productId: string
  productName: string
  generatedImageIndex: number
  mediaType?: "image" | "video"
  createdAt: Date | null
}

export interface GalleryDateGroup {
  dateLabel: string
  images: GalleryImage[]
}

const toDate = (ts: { seconds: number } | null | undefined): Date | null => {
  if (!ts) return null
  return new Date(ts.seconds * 1000)
}

const useGalleryImages = (products: Product[]): { groups: GalleryDateGroup[]; allImages: GalleryImage[] } => {
  return useMemo(() => {
    const images: GalleryImage[] = []

    for (const product of products) {
      const generatedImages = Array.isArray(product.generatedImages)
        ? product.generatedImages
        : (Object.values(product.generatedImages ?? {}) as GeneratedProductImage[])

      for (let i = 0; i < generatedImages.length; i++) {
        const img = generatedImages[i]
        if (!img.url) continue
        const createdAt = toDate(img.createdAt ?? img.regeneratedAt) ?? toDate(product.createdAt)
        images.push({
          assetId: `${product.id}:${i}:image`,
          url: img.url,
          productId: product.id,
          productName: product.name,
          generatedImageIndex: i,
          mediaType: "image",
          createdAt
        })
      }
    }

    images.sort((a, b) => {
      if (!a.createdAt && !b.createdAt) return 0
      if (!a.createdAt) return 1
      if (!b.createdAt) return -1
      return b.createdAt.getTime() - a.createdAt.getTime()
    })

    const grouped = groupBy(images, img => (img.createdAt ? format(img.createdAt, "EEE, d MMM yyyy") : "Unknown Date"))

    const groups = Object.entries(grouped).map(([dateLabel, imgs]) => ({
      dateLabel,
      images: imgs
    }))

    return { groups, allImages: images }
  }, [products])
}

export { useGalleryImages }
